import { ErrorMessage } from '@hookform/error-message';
import { FormErrorResponse, GLOBAL_ERROR_ATTR } from 'lib/api/errors/error-response';
import { APIError } from 'lib/api/exceptions/http-exceptions';
import { ClassList } from 'lib/utils/react/classlist';
import React from 'react';
import type {
  FieldErrors,
  MultipleFieldErrors,
  UseFormSetError,
  ValidateResult
} from 'react-hook-form';

/**
 * Extract form and field errors from mutate errors.
 *
 * @param apiError The API error (exception)
 * @param setError The mutate.setError method for your form
 */
export const onMutateError = (
  apiError: APIError,
  setError: UseFormSetError<any>,
  {
    globalCode = 'global_error',
    globalDetail = undefined
  }: { globalCode?: string; globalDetail?: string } = {}
) => {
  // Get errors from exception
  const errors: FormErrorResponse = apiError.errors;
  if (errors && globalDetail) {
    errors.appendGlobalError(globalDetail, globalCode);
  }

  // Errors
  const fields: { [attr: string]: { types: MultipleFieldErrors } } = {};

  errors.forEach(({ attr, code, detail }) => {
    // Create field name if it does not exist (since { "attr": null } from
    // server for global errors we give it the name "root.global")
    if (!fields.hasOwnProperty(attr)) {
      fields[attr] = { types: {} };
    }
    // Add error to field
    fields[attr].types[code] = detail;
  });

  // Set errors
  Object.keys(fields).forEach((key) => setError(key, fields[key]));
};

/**
 * Error props for FormError and InputError
 */
export type ErrorProps = {
  message: ValidateResult;
  index?: number;
};

/**
 * A form error React component.
 */
export const FormError: React.FC<ErrorProps> = ({ message, index }) =>
  message ? (
    <p className={ClassList.of('form-error-message').add('first', index === 0).names}>
      {message}
    </p>
  ) : null;

/**
 * A list of form errors React component.
 */
export const FormErrors: React.FC<{
  name?: string;
  errors: FieldErrors;
}> = ({
  name = GLOBAL_ERROR_ATTR, // default name for global errors
  errors
}) => (
  <ErrorMessage
    name={name}
    errors={errors}
    render={({ messages }) =>
      messages &&
      Object.entries(messages).map(([type, message], index) => (
        <FormError key={type} message={message?.toString()} index={index} />
      ))
    }
  />
);

/**
 * A form input error React component.
 */
export const InputError: React.FC<ErrorProps> = ({ message, index }) =>
  message ? (
    <p className={ClassList.of('input-error-message').add('first', index === 0).names}>
      {message}
    </p>
  ) : null;

/**
 * A list of form input errors React component.
 */
export const InputErrors: React.FC<{ name: string; errors: FieldErrors }> = ({
  name,
  errors
}) => {
  const error = errors[name];

  if (error && error?.types) {
    return (
      <ErrorMessage
        name={name}
        errors={errors}
        render={({ messages }) =>
          messages &&
          Object.entries(messages).map(([type, message], index) => (
            <InputError key={type} message={message?.toString()} index={index} />
          ))
        }
      />
    );
  } else {
    return (
      <ErrorMessage
        name={name}
        errors={errors}
        render={({ message }) => <InputError message={message?.toString()} />}
      />
    );
  }
};
